import { type LDClient, type LDMultiKindContext } from "launchdarkly-react-client-sdk";
import { FEATURE_FLAG_DEFINITIONS, FeatureFlags } from "../config/featureFlags";
import { VERSION } from "../config/version";
import { handleError } from "../errors";
import Logger from "./sentry";
import type { UserProfile } from "../interfaces/User";
import { getTenant } from "../utils/tenant";

let ldClient: LDClient | undefined;

export const setLaunchDarklyClient = (client: LDClient): void => {
  ldClient = client;
};

const getEnvironment = () => {
  switch (window.location.hostname) {
    case "localhost":
      return "development";
    case "excel.stage.ediphi.com":
      return "stage";
    case "excel.ediphi.com":
      return "prod";
    default:
      return "unknown";
  }
};

export const buildLaunchDarklyContext = (userProfile?: UserProfile): LDMultiKindContext => {
  const multiContext: LDMultiKindContext = { kind: "multi" };

  multiContext.user = {
    key: userProfile?.email ?? "notset@none.com",
    name: userProfile?.full_name ?? "Unknown User",
    email: userProfile?.email,
  };
  multiContext.tenant = { key: getTenant() ?? "excel" };
  multiContext.environment = { key: getEnvironment() };
  multiContext.version = { key: VERSION };

  return multiContext;
};

function getDefaultValueForLaunchDarklyFlag<T extends keyof FeatureFlags>(featureFlag: T): FeatureFlags[T] | undefined {
  const flagDefinition = FEATURE_FLAG_DEFINITIONS[featureFlag];
  const env = getEnvironment();
  const isNonProd = env === "stage" || env === "development";
  if (flagDefinition) {
    return (isNonProd ? flagDefinition.nonProdDefaultValue : flagDefinition.defaultValue) as FeatureFlags[T];
  }
  return undefined;
}

export function evaluateFlag<T extends keyof FeatureFlags>(featureFlag: T): FeatureFlags[T] | undefined {
  const defaultFlagValue = getDefaultValueForLaunchDarklyFlag(featureFlag);

  if (!ldClient) return defaultFlagValue;

  const flagValue: FeatureFlags[T] = ldClient.variation(featureFlag, defaultFlagValue);

  try {
    FEATURE_FLAG_DEFINITIONS[featureFlag].validator.parse(flagValue);
  } catch (err) {
    // EXCEPTION: Logger error allowed here — the default is returned rather than thrown, and
    // evaluateFlag is called from excel/ too, where no hook is in the chain to log it. A flag whose
    // value fails its own schema is invisible otherwise.
    const error = handleError(err, {
      flagName: featureFlag,
      flagValue,
      defaultValue: defaultFlagValue,
      zodError: err instanceof Error ? err.message : String(err),
    });
    Logger.error("LaunchDarkly feature flag value failed validation. Falling back to default value.", error);
    return defaultFlagValue;
  }
  return flagValue;
}
